import React from "react";
import {
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Tooltip,
  TextField,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { SelectChangeEvent } from "@mui/material/Select";

interface IdentificationDocumentsProps {
  values: {
    idType: string;
    govFile: File | string | null;
    addressFile: File | string | null;
    idNumber: string;
    expirationDate: string;
    ssn: string;
  };
  errors: {
    idType?: string;
    govFile?: string;
    addressFile?: string;
    expirationDate?: string;
    idNumber?: string;
    ssn?: string;
  };
  touched: {
    idType?: boolean;
    govFile?: boolean;
    addressFile?: boolean;
    expirationDate?: boolean;
    idNumber?: boolean;
    ssn?: boolean;
  };
  handleChange: (e: React.ChangeEvent<any>) => void;
  setFieldValue: (field: string, value: any) => void;
}

const FilePreview: React.FC<{
  file: File | string | null;
  onRemove: () => void;
}> = ({ file, onRemove }) => {
  if (!file) return null;

  const isFileObj = file instanceof File;
  const fileName = isFileObj ? file.name : file;
  const fileUrl = isFileObj ? URL.createObjectURL(file) : file ? `${file}` : undefined;
  const isPdf = fileName.toLowerCase().endsWith(".pdf");

  return (
    <div className="mt-3 p-3 border rounded-lg bg-gray-50 overflow-hidden max-w-full">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 flex-wrap">
        <div className="flex items-center gap-2 sm:gap-3 flex-1">
          {isPdf ? (
            <PictureAsPdfIcon className="text-red-500 flex-shrink-0" />
          ) : (
            <InsertPhotoIcon className="text-blue-500 flex-shrink-0" />
          )}
          <Typography
            variant="body2"
            className="text-gray-800 font-medium min-w-0 truncate flex-1"
          >
            {fileName.split("/").pop()}
          </Typography>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <Tooltip title="Open">
            <IconButton
              size="small"
              component="a"
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove">
            <IconButton size="small" onClick={onRemove} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

const IdentificationDocuments: React.FC<IdentificationDocumentsProps> = ({
  values,
  errors,
  touched,
  setFieldValue,
}) => {
  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    setFieldValue("idType", event.target.value);
  };

  const renderUpload = (field: "govFile" | "addressFile", label: string) => (
    <Paper className="p-3 sm:p-4 rounded-xl border border-gray-200 shadow-sm">
      <Typography
        variant="subtitle1"
        className="mb-2 sm:mb-3 font-medium text-gray-800 text-center sm:text-left truncate max-w-full"
      >
        {label}
      </Typography>

      {!values[field] ? (
        <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl py-6 sm:py-8 px-3 sm:px-4 cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition">
          <CloudUploadIcon className="text-blue-400 mb-2" fontSize="large" />
          <span className="text-sm font-medium text-gray-700 text-center">
            Click to upload or drag & drop
          </span>
          <span className="text-xs text-gray-500 mt-1 text-center">
            PNG, JPG, or PDF (max 5MB)
          </span>
          <input
            type="file"
            hidden
            accept="image/*,application/pdf"
            onChange={(e) =>
              e.target.files && setFieldValue(field, e.target.files[0])
            }
          />
        </label>
      ) : (
        <FilePreview
          file={values[field]}
          onRemove={() => setFieldValue(field, null)}
        />
      )}
      {touched[field] && errors[field] && (
        <FormHelperText error className="text-center sm:text-left">{errors[field]}</FormHelperText>
      )}
    </Paper>
  );

  return (
    <Paper className="p-4 sm:p-6 rounded-2xl shadow-md bg-white">
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        sx={{ mb: { xs: 3, sm: 4 } }}
      >
        <DescriptionIcon color="primary" fontSize="medium" />
        <Typography variant="h6" className="font-semibold text-gray-800 text-center sm:text-left">
          Identification Documents
        </Typography>
      </Box>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Government ID */}
        <Paper className="p-3 sm:p-4 rounded-xl border border-gray-200 shadow-sm">
          <Typography
            variant="subtitle1"
            className="mb-2 sm:mb-3 font-medium text-gray-800 text-center sm:text-left truncate max-w-full"
          >
            Government-issued ID
          </Typography>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4 mt-3">
            {/* ID Number */}
            <TextField
              label="ID Number *"
              name="idNumber"
              value={values.idNumber}
              onChange={(e) => setFieldValue("idNumber", e.target.value)}
              error={touched.idNumber && Boolean(errors.idNumber)}
              helperText={touched.idNumber && errors.idNumber}
              fullWidth
              size="small"
              InputProps={{ style: { borderRadius: 10 } }}
            />

            {/* ID Expiration Date */}
            <TextField
              label="ID Expiration Date *"
              name="expirationDate"
              type="date"
              value={values.expirationDate}
              onChange={(e) => {
                const dateValue = e.target.value;
                if (dateValue) {
                  const enteredDate = new Date(dateValue);
                  const maxFutureDate = new Date();
                  maxFutureDate.setFullYear(maxFutureDate.getFullYear() + 50);
                  if (enteredDate > maxFutureDate) {
                    return;
                  }
                }
                setFieldValue("expirationDate", dateValue);
              }}
              error={touched.expirationDate && Boolean(errors.expirationDate)}
              helperText={touched.expirationDate && errors.expirationDate}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="MM/DD/YYYY"
              inputProps={{
                max: new Date(new Date().getFullYear() + 50, 11, 31).toISOString().split("T")[0],
              }}
            />
          </div>

          <FormControl
            fullWidth
            size="small"
            error={touched.idType && Boolean(errors.idType)}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" }, marginBottom: '12px' }}
          >
            <InputLabel>ID Type *</InputLabel>
            <Select
              name="idType"
              value={values.idType}
              onChange={handleSelectChange}
              label="ID Type"
            >
              <MenuItem value="driving-license">Driving License</MenuItem>
              <MenuItem value="state-id">State ID</MenuItem>
              <MenuItem value="passport">Passport</MenuItem>
              <MenuItem value="itin">ITIN</MenuItem>
            </Select>
            {touched.idType && errors.idType && (
              <FormHelperText className="text-center sm:text-left">{errors.idType}</FormHelperText>
            )}
          </FormControl>

          {/* SSN */}
          <TextField
            label="Social Security Number (SSN) *"
            name="ssn"
            value={values.ssn}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 9);
              let formatted = "";
              if (val.length > 0) formatted += val.slice(0, 3);
              if (val.length > 3) formatted += "-" + val.slice(3, 5);
              if (val.length > 5) formatted += "-" + val.slice(5, 9);
              setFieldValue("ssn", formatted);
            }}
            error={touched.ssn && Boolean(errors.ssn)}
            helperText={touched.ssn && errors.ssn}
            fullWidth
            size="small"
            InputProps={{ style: { borderRadius: 10 } }}
            placeholder="XXX-XX-XXXX"
            inputProps={{ maxLength: 11 }}
            sx={{ marginBottom: '12px' }}
          />

          {/* File upload / preview */}
          {renderUpload("govFile", "Upload Government ID")}
        </Paper>

        {/* Proof of Address */}
        {renderUpload("addressFile", "Proof of Address")}
      </div>
    </Paper>
  );
};

export default IdentificationDocuments;
